import React, { useState, useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  propertyApi,
  type PropertyEvaluation,
  type PropertyData,
} from '../services';
import './PropertyEvaluationChart.css';

type TimePeriod = '1month' | '6months' | '12months' | '5years' | 'all';

interface PropertyEvaluationChartProps {
  property: PropertyData;
  refreshTrigger?: number; // Increment this to trigger a refresh
  timePeriod?: TimePeriod;
}

const PropertyEvaluationChart: React.FC<PropertyEvaluationChartProps> = ({
  property,
  refreshTrigger,
  timePeriod = '12months',
}) => {
  const [evaluations, setEvaluations] = useState<PropertyEvaluation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const chartWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchEvaluations = async () => {
      if (!property.id || property.id === 'new') {
        return;
      }

      setLoading(true);
      try {
        const data = await propertyApi.getEvaluations(property.id, timePeriod);
        setEvaluations(data);
      } catch (error) {
        console.error('Failed to fetch evaluations:', error);
        setEvaluations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluations();
  }, [property.id, timePeriod, refreshTrigger]);

  // Prepare chart data from evaluations
  const evalData = evaluations
    .map((evaluation) => ({
      date: new Date(evaluation.timestamp),
      vw: evaluation.vw ? Number(evaluation.vw) : null,
      vw_lwr: evaluation.vwLwr ? Number(evaluation.vwLwr) : null,
      vw_upr: evaluation.vwUpr ? Number(evaluation.vwUpr) : null,
    }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      const millions = value / 1000000;
      return `€ ${millions.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} Mio`;
    }
    return `€ ${value.toLocaleString('de-DE', { maximumFractionDigits: 0 })}`;
  };
  const getEChartsOption = () => {
    if (evalData.length === 0) return {};

    const oldestDate = evalData[0].date.getTime();
    const newestDate = evalData[evalData.length - 1].date.getTime();

    const kaufTime = property.kaufdatum
      ? new Date(property.kaufdatum).getTime()
      : null;
    const sellTime = property.sellDate
      ? new Date(property.sellDate).getTime()
      : null;

    const threeMonthsMs = 90 * 24 * 60 * 60 * 1000;
    const hasRecentAction =
      (kaufTime !== null &&
        kaufTime > newestDate &&
        kaufTime <= newestDate + threeMonthsMs) ||
      (sellTime !== null &&
        sellTime > newestDate &&
        sellTime <= newestDate + threeMonthsMs);

    // 3 months extension past the newest valuation ONLY if there is a buy/sell action
    const xAxisMax = hasRecentAction ? newestDate + threeMonthsMs : newestDate;

    // Base series for Valuation
    const series: any[] = [
      {
        name: 'Estimated Lower Bound',
        type: 'line',
        data: evalData.map((d) => [d.date.getTime(), d.vw_lwr]),
        smooth: true,
        showSymbol: false,
        lineStyle: {
          color: '#A7A3E2',
          width: 1.5,
          type: 'dashed',
          opacity: 0.5,
        },
      },
      {
        name: 'Estimated Upper Bound',
        type: 'line',
        data: evalData.map((d) => [d.date.getTime(), d.vw_upr]),
        smooth: true,
        showSymbol: false,
        lineStyle: {
          color: '#A7A3E2',
          width: 1.5,
          type: 'dashed',
          opacity: 0.5,
        },
      },
      {
        name: 'Valuation',
        type: 'line',
        data: evalData.map((d) => [d.date.getTime(), d.vw]),
        smooth: true,
        showSymbol: true,
        symbol: 'circle',
        symbolSize: 8,
        itemStyle: {
          color: '#100C21', // Hollow effect blending with graph background
          borderColor: '#4FFF84', // Green matching Figma Card (1).svg
          borderWidth: 2,
        },
        lineStyle: {
          color: '#4FFF84', // Neon green stroke matching Figma Card (1).svg
          width: 3,
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(79, 255, 132, 0.15)' }, // Fade from neon green
              { offset: 1, color: 'rgba(79, 255, 132, 0.01)' },
            ],
          },
        },
        markLine:
          evalData.length > 0 && evalData[0].vw !== null
            ? {
                symbol: ['none', 'none'],
                data: [
                  {
                    yAxis: evalData[0].vw,
                    lineStyle: {
                      color: '#A7A3E2',
                      type: 'dashed',
                      opacity: 0.4,
                      width: 1.5,
                    },
                    label: { show: false },
                  },
                ],
              }
            : undefined,
      },
    ];

    // Overlay Buy/Sell data if within local market data range
    if (
      kaufTime !== null &&
      property.kaufpreis &&
      kaufTime >= oldestDate &&
      kaufTime <= xAxisMax
    ) {
      const endBuyTime = sellTime && sellTime <= xAxisMax ? sellTime : xAxisMax;
      series.push({
        name: 'Buy Price',
        type: 'line',
        data: [
          [kaufTime, property.kaufpreis],
          [endBuyTime, property.kaufpreis],
        ],
        smooth: false,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { color: '#4caf50', width: 2, type: 'dashed' },
        itemStyle: { color: '#4caf50' },
      });
    }

    if (
      sellTime !== null &&
      property.sellAmount &&
      sellTime >= oldestDate &&
      sellTime <= xAxisMax
    ) {
      series.push({
        name: 'Sell Price',
        type: 'line',
        data: [
          [sellTime, property.sellAmount],
          [xAxisMax, property.sellAmount],
        ],
        smooth: false,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { color: '#f44336', width: 2, type: 'dashed' },
        itemStyle: { color: '#f44336' },
      });
    }

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(20, 20, 30, 0.9)',
        borderColor: 'rgba(167, 163, 226, 0.3)',
        textStyle: { color: '#F3F1FF' },
        axisPointer: {
          type: 'line',
          lineStyle: { color: '#A7A3E2', type: 'dashed' },
        },
        valueFormatter: (value: number) => formatCurrency(value),
      },
      legend: {
        bottom: 0,
        textStyle: { color: '#A7A3E2', fontFamily: 'Inter, sans-serif' },
        icon: 'circle',
      },
      grid: {
        show: true, // Show grid border matching Card (1).svg
        borderColor: 'rgba(167, 163, 226, 0.1)',
        borderWidth: 1,
        backgroundColor: 'transparent',
        left: '4%',
        right: '4%',
        top: '10%',
        bottom: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'time',
        boundaryGap: false,
        max: xAxisMax,
        axisLine: { show: false }, // Hide solid axis line, rely on grid border
        axisTick: { show: false },
        axisLabel: {
          color: '#A7A3E2',
          fontFamily: 'Inter, sans-serif',
          formatter: '{MMM} {yyyy}',
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#A7A3E2',
          fontFamily: 'Inter, sans-serif',
          formatter: (value: number) => formatCurrency(value),
        },
        splitLine: { show: false }, // Hide horizontal lines, rely on dashed baseline markLine
      },
      series,
    };
  };
  if (loading) {
    return (
      <div className="evaluation-chart-container">
        <div className="chart-loading">Loading chart data...</div>
      </div>
    );
  }

  if (evaluations.length === 0) {
    return (
      <div className="evaluation-chart-container">
        <div className="chart-empty" style={{ color: '#F3F1FF' }}>
          No valuation history available yet.
        </div>
      </div>
    );
  }

  return (
    <div
      className="evaluation-chart-container"
      style={{ background: 'transparent', width: '100%' }}
    >
      <div
        className="chart-wrapper"
        ref={chartWrapperRef}
        style={{ width: '100%', height: '370px' }}
      >
        <ReactECharts
          option={getEChartsOption()}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'canvas' }}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
    </div>
  );
};

export default PropertyEvaluationChart;
